import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

export type InterviewStage = 'guidelines' | 'coding' | 'database-questions' | 'system-design' | 'data-modeling' | 'uml' | 'whiteboard' | 'notes' | 'solution-design' | 'code-review' | 'video-interview' | 'pair-programming' | 'completed'

export interface StageDataItem {
  content: any
  isDirty: boolean
  lastSaved: string | null
}

export interface CodingProblemData {
  code: string
  language: string | null
  boilerplate: string
  isDirty: boolean
  lastSaved: string | null
}

export interface DatabaseProblemData {
  query: string
  isDirty: boolean
  lastSaved: string | null
}

export interface SystemDesignComponent {
  id: string
  componentId: string
  name: string
  icon: string
  color: string
  x: number
  y: number
  width: number
  height: number
}

export interface SystemDesignConnection {
  id: string
  type: 'arrow' | 'line'
  fromComponentId: string
  toComponentId: string
  label?: string
  color: string
  strokeWidth: number
}

export interface SystemDesignData {
  content: string
  placedComponents: SystemDesignComponent[]
  connections: SystemDesignConnection[]
  designMode: 'visual' | 'text'
  selectedTool: 'move' | 'connect' | 'delete'
  isDirty: boolean
  lastSaved: string | null
}

export interface WhiteboardData {
  content: string
  canvasData: string | null // Base64 encoded canvas image
  drawingMode: 'draw' | 'text'
  selectedTool: string
  selectedColor: string
  strokeWidth: number
  isDirty: boolean
  lastSaved: string | null
}

export interface UMLClass {
  id: string
  name: string
  type: 'class' | 'interface' | 'abstract' | 'actor' | 'usecase'
  x: number
  y: number
  attributes: Array<{
    id: string
    name: string
    type: string
    visibility: 'public' | 'private' | 'protected'
  }>
  methods: Array<{
    id: string
    name: string
    returnType: string
    parameters: string
    visibility: 'public' | 'private' | 'protected'
  }>
}

export interface UMLRelationship {
  id: string
  fromClassId: string
  toClassId: string
  type: 'inheritance' | 'composition' | 'aggregation' | 'association' | 'dependency'
  label: string
}

export interface UMLData {
  content: string
  classes: UMLClass[]
  relationships: UMLRelationship[]
  diagramType: 'class' | 'sequence' | 'usecase'
  isDirty: boolean
  lastSaved: string | null
}

export interface DataModelingEntity {
  id: string
  name: string
  x: number
  y: number
  fields: Array<{
    id: string
    name: string
    type: string
    isPrimary: boolean
    isRequired: boolean
    defaultValue?: string
  }>
}

export interface DataModelingRelationship {
  id: string
  fromEntityId: string
  toEntityId: string
  type: 'one-to-one' | 'one-to-many' | 'many-to-many'
  fromField: string
  toField: string
}

export interface DataModelingComment {
  id: string
  x: number
  y: number
  text: string
  color: string
}

export interface DataModelingData {
  content: string
  entities: DataModelingEntity[]
  relationships: DataModelingRelationship[]
  fkRelationships: DataModelingRelationship[]
  comments: DataModelingComment[]
  zoom: number
  panX: number
  panY: number
  isDirty: boolean
  lastSaved: string | null
}

export interface ParsedSystemDesignNotes {
  title?: string
  description?: string
  requirements?: string[]
  considerations?: string[]
  additionalNotes?: string
}

export interface SystemDesignPrompt {
  uuid?: string | null
  role?: string | null
  status?: string | null
  notes?: string | null
  parsedNotes?: ParsedSystemDesignNotes | null
  assignedAt?: string | null
  acceptedAt?: string | null
  completedAt?: string | null
  interviewer?: {
    id?: number | null
    uuid?: string | null
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    title?: string | null
  } | null
}

export interface StageData {
  coding: {
    [problemId: string]: CodingProblemData
  }
  'database-questions': {
    [problemId: string]: DatabaseProblemData
  }
  'system-design': SystemDesignData
  'data-modeling': DataModelingData
  'uml': UMLData
  whiteboard: WhiteboardData
  notes: StageDataItem
  'solution-design': StageDataItem
  'code-review': StageDataItem
  'video-interview': StageDataItem
  'pair-programming': StageDataItem
}

export interface InterviewStore {
  // Core state
  activeStage: InterviewStage
  activeProblemId: string | null
  sessionId: string | null
  interviewId: string | null
  
  // All stage data
  stageData: StageData
  codingProblemMeta: Record<string, {
    problemId: number | null
    assignmentOrder: number | null
    assignmentUuid: string | null
    starterCodeMap: Record<string, string>
  }>
  
  // Actions
  setActiveStage: (stage: InterviewStage) => void
  setActiveProblemId: (problemId: string | null) => void
  setSessionInfo: (sessionId: string, interviewId: string) => void
  
  // Data update actions
  updateCodingData: (problemId: string, code: string, language?: string | null, boilerplate?: string) => void
  updateDatabaseData: (problemId: string, query: string) => void
  updateStageData: (stage: Exclude<keyof StageData, 'coding' | 'database-questions' | 'system-design' | 'whiteboard' | 'data-modeling' | 'uml'>, content: any) => void
  
  // System design specific actions
  updateSystemDesignContent: (content: string) => void
  updateSystemDesignComponents: (components: SystemDesignComponent[]) => void
  setSystemDesignMode: (mode: 'visual' | 'text') => void
  setSystemDesignTool: (tool: 'move' | 'connect' | 'delete') => void
  addSystemDesignComponent: (component: SystemDesignComponent) => void
  removeSystemDesignComponent: (componentId: string) => void
  updateSystemDesignComponent: (componentId: string, updates: Partial<SystemDesignComponent>) => void
  addSystemDesignConnection: (connection: SystemDesignConnection) => void
  removeSystemDesignConnection: (connectionId: string) => void
  updateSystemDesignConnections: (connections: SystemDesignConnection[]) => void
  
  // Whiteboard specific actions
  updateWhiteboardContent: (content: string) => void
  updateWhiteboardCanvas: (canvasData: string) => void
  setWhiteboardMode: (mode: 'draw' | 'text') => void
  setWhiteboardTool: (tool: string) => void
  setWhiteboardColor: (color: string) => void
  setWhiteboardStrokeWidth: (width: number) => void
  
  // UML design specific actions
  updateUMLContent: (content: string) => void
  updateUMLClasses: (classes: UMLClass[]) => void
  updateUMLRelationships: (relationships: UMLRelationship[]) => void
  addUMLClass: (umlClass: UMLClass) => void
  removeUMLClass: (classId: string) => void
  updateUMLClass: (classId: string, updates: Partial<UMLClass>) => void
  addUMLRelationship: (relationship: UMLRelationship) => void
  removeUMLRelationship: (relationshipId: string) => void
  setUMLDiagramType: (type: 'class' | 'sequence' | 'usecase') => void
  
  // Data modeling specific actions
  updateDataModelingContent: (content: string) => void
  updateDataModelingEntities: (entities: DataModelingEntity[]) => void
  updateDataModelingRelationships: (relationships: DataModelingRelationship[]) => void
  updateDataModelingFKRelationships: (fkRelationships: DataModelingRelationship[]) => void
  updateDataModelingComments: (comments: DataModelingComment[]) => void
  updateDataModelingZoom: (zoom: number) => void
  updateDataModelingPan: (panX: number, panY: number) => void
  addDataModelingEntity: (entity: DataModelingEntity) => void
  removeDataModelingEntity: (entityId: string) => void
  updateDataModelingEntity: (entityId: string, updates: Partial<DataModelingEntity>) => void
  addDataModelingRelationship: (relationship: DataModelingRelationship) => void
  removeDataModelingRelationship: (relationshipId: string) => void
  addDataModelingComment: (comment: DataModelingComment) => void
  removeDataModelingComment: (commentId: string) => void
  
  // Dirty state management
  markCodingDirty: (problemId: string) => void
  markDatabaseDirty: (problemId: string) => void
  markStageDirty: (stage: Exclude<keyof StageData, 'coding' | 'database-questions'>) => void
  markCodingClean: (problemId: string) => void
  markDatabaseClean: (problemId: string) => void
  markStageClean: (stage: Exclude<keyof StageData, 'coding' | 'database-questions'>) => void
  
  // Data getters
  getCodingData: (problemId: string) => CodingProblemData
  getCodingProblemMeta: (problemId: string) => { problemId: number | null; assignmentOrder: number | null; assignmentUuid: string | null } | undefined
  setCodingProblemMeta: (problemId: string, meta: { problemId: number | null; assignmentOrder: number | null; assignmentUuid: string | null; starterCodeMap?: Record<string, string> }) => void
  getDatabaseData: (problemId: string) => DatabaseProblemData
  getStageData: (stage: Exclude<keyof StageData, 'coding' | 'database-questions'>) => SystemDesignData | WhiteboardData | UMLData | DataModelingData | StageDataItem
  getSystemDesignData: () => SystemDesignData
  getUMLData: () => UMLData
  getDataModelingData: () => DataModelingData
  getWhiteboardData: () => WhiteboardData
  systemDesignPrompt: SystemDesignPrompt | null
  setSystemDesignPrompt: (prompt: SystemDesignPrompt | null) => void
  getSystemDesignPrompt: () => SystemDesignPrompt | null
  
  // Utility
  resetStore: () => void
}

const createInitialStageData = (): StageData => ({
  coding: {},
  'database-questions': {},
  'system-design': { 
    content: '', 
    placedComponents: [], 
    connections: [],
    designMode: 'visual',
    selectedTool: 'move',
    isDirty: false, 
    lastSaved: null 
  },
  'data-modeling': { 
    content: '', 
    entities: [], 
    relationships: [], 
    fkRelationships: [], 
    comments: [],
    zoom: 1,
    panX: 0,
    panY: 0,
    isDirty: false, 
    lastSaved: null 
  },
  'uml': { 
    content: '', 
    classes: [], 
    relationships: [], 
    diagramType: 'class',
    isDirty: false, 
    lastSaved: null 
  },
  whiteboard: { 
    content: '', 
    canvasData: null,
    drawingMode: 'draw',
    selectedTool: 'pen',
    selectedColor: '#000000',
    strokeWidth: 2,
    isDirty: false, 
    lastSaved: null 
  },
  notes: { content: '', isDirty: false, lastSaved: null },
  'solution-design': { content: '', isDirty: false, lastSaved: null },
  'code-review': { content: '', isDirty: false, lastSaved: null },
  'video-interview': { content: '', isDirty: false, lastSaved: null },
  'pair-programming': { content: '', isDirty: false, lastSaved: null }
})

export const useInterviewStore = create<InterviewStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    activeStage: 'guidelines',
    activeProblemId: null,
    sessionId: null,
    interviewId: null,
    stageData: createInitialStageData(),
    codingProblemMeta: {},
    systemDesignPrompt: null,

    // Basic setters
    setActiveStage: (stage) => set({ activeStage: stage }),
    setActiveProblemId: (problemId) => set({ activeProblemId: problemId }),
    setSessionInfo: (sessionId, interviewId) => set({ sessionId, interviewId }),

    // Data updates
    updateCodingData: (problemId, code, language?, boilerplate?) => set((state) => ({
      stageData: {
        ...state.stageData,
        coding: {
          ...state.stageData.coding,
          [problemId]: {
            code,
            language: language !== undefined ? language : state.stageData.coding[problemId]?.language || null,
            boilerplate: boilerplate !== undefined ? boilerplate : state.stageData.coding[problemId]?.boilerplate || '',
            isDirty: true,
            lastSaved: state.stageData.coding[problemId]?.lastSaved || null
          }
        }
      }
    })),

    updateDatabaseData: (problemId, query) => set((state) => ({
      stageData: {
        ...state.stageData,
        'database-questions': {
          ...state.stageData['database-questions'],
          [problemId]: {
            query,
            isDirty: true,
            lastSaved: state.stageData['database-questions'][problemId]?.lastSaved || null
          }
        }
      }
    })),

    updateStageData: (stage, content) => set((state) => ({
      stageData: {
        ...state.stageData,
        [stage]: {
          content,
          isDirty: true,
          lastSaved: (state.stageData[stage] as StageDataItem).lastSaved
        }
      }
    })),

    // System design specific actions
    updateSystemDesignContent: (content) => set((state) => ({
      stageData: {
        ...state.stageData,
        'system-design': {
          ...state.stageData['system-design'],
          content,
          isDirty: true
        }
      }
    })),

    updateSystemDesignComponents: (components) => set((state) => ({
      stageData: {
        ...state.stageData,
        'system-design': {
          ...state.stageData['system-design'],
          placedComponents: components,
          isDirty: true
        }
      }
    })),

    setSystemDesignMode: (mode) => set((state) => ({
      stageData: {
        ...state.stageData,
        'system-design': {
          ...state.stageData['system-design'],
          designMode: mode,
          isDirty: true
        }
      }
    })),

    setSystemDesignTool: (tool) => set((state) => ({
      stageData: {
        ...state.stageData,
        'system-design': {
          ...state.stageData['system-design'],
          selectedTool: tool
        }
      }
    })),

    addSystemDesignComponent: (component) => set((state) => ({
      stageData: {
        ...state.stageData,
        'system-design': {
          ...state.stageData['system-design'],
          placedComponents: [...state.stageData['system-design'].placedComponents, component],
          isDirty: true
        }
      }
    })),

    removeSystemDesignComponent: (componentId) => set((state) => ({
      stageData: {
        ...state.stageData,
        'system-design': {
          ...state.stageData['system-design'],
          placedComponents: state.stageData['system-design'].placedComponents.filter(c => c.id !== componentId),
          isDirty: true
        }
      }
    })),

    updateSystemDesignComponent: (componentId, updates) => set((state) => ({
      stageData: {
        ...state.stageData,
        'system-design': {
          ...state.stageData['system-design'],
          placedComponents: state.stageData['system-design'].placedComponents.map(c =>
            c.id === componentId ? { ...c, ...updates } : c
          ),
          isDirty: true
        }
      }
    })),

    addSystemDesignConnection: (connection) => set((state) => ({
      stageData: {
        ...state.stageData,
        'system-design': {
          ...state.stageData['system-design'],
          connections: [...state.stageData['system-design'].connections, connection],
          isDirty: true
        }
      }
    })),

    removeSystemDesignConnection: (connectionId) => set((state) => ({
      stageData: {
        ...state.stageData,
        'system-design': {
          ...state.stageData['system-design'],
          connections: state.stageData['system-design'].connections.filter(c => c.id !== connectionId),
          isDirty: true
        }
      }
    })),

    updateSystemDesignConnections: (connections) => set((state) => ({
      stageData: {
        ...state.stageData,
        'system-design': {
          ...state.stageData['system-design'],
          connections,
          isDirty: true
        }
      }
    })),

    // Whiteboard specific actions
    updateWhiteboardContent: (content) => set((state) => ({
      stageData: {
        ...state.stageData,
        whiteboard: {
          ...state.stageData.whiteboard,
          content,
          isDirty: true
        }
      }
    })),

    updateWhiteboardCanvas: (canvasData) => set((state) => ({
      stageData: {
        ...state.stageData,
        whiteboard: {
          ...state.stageData.whiteboard,
          canvasData,
          isDirty: true
        }
      }
    })),

    setWhiteboardMode: (mode) => set((state) => ({
      stageData: {
        ...state.stageData,
        whiteboard: {
          ...state.stageData.whiteboard,
          drawingMode: mode
        }
      }
    })),

    setWhiteboardTool: (tool) => set((state) => ({
      stageData: {
        ...state.stageData,
        whiteboard: {
          ...state.stageData.whiteboard,
          selectedTool: tool
        }
      }
    })),

    setWhiteboardColor: (color) => set((state) => ({
      stageData: {
        ...state.stageData,
        whiteboard: {
          ...state.stageData.whiteboard,
          selectedColor: color
        }
      }
    })),

    setWhiteboardStrokeWidth: (width) => set((state) => ({
      stageData: {
        ...state.stageData,
        whiteboard: {
          ...state.stageData.whiteboard,
          strokeWidth: width
        }
      }
    })),

    // UML design methods
    updateUMLContent: (content) => set((state) => ({
      stageData: {
        ...state.stageData,
        'uml': {
          ...state.stageData['uml'],
          content,
          isDirty: true
        }
      }
    })),

    updateUMLClasses: (classes) => set((state) => ({
      stageData: {
        ...state.stageData,
        'uml': {
          ...state.stageData['uml'],
          classes,
          isDirty: true
        }
      }
    })),

    updateUMLRelationships: (relationships) => set((state) => ({
      stageData: {
        ...state.stageData,
        'uml': {
          ...state.stageData['uml'],
          relationships,
          isDirty: true
        }
      }
    })),

    addUMLClass: (umlClass) => set((state) => ({
      stageData: {
        ...state.stageData,
        'uml': {
          ...state.stageData['uml'],
          classes: [...state.stageData['uml'].classes, umlClass],
          isDirty: true
        }
      }
    })),

    removeUMLClass: (classId) => set((state) => ({
      stageData: {
        ...state.stageData,
        'uml': {
          ...state.stageData['uml'],
          classes: state.stageData['uml'].classes.filter(c => c.id !== classId),
          relationships: state.stageData['uml'].relationships.filter(r => 
            r.fromClassId !== classId && r.toClassId !== classId
          ),
          isDirty: true
        }
      }
    })),

    updateUMLClass: (classId, updates) => set((state) => ({
      stageData: {
        ...state.stageData,
        'uml': {
          ...state.stageData['uml'],
          classes: state.stageData['uml'].classes.map(c =>
            c.id === classId ? { ...c, ...updates } : c
          ),
          isDirty: true
        }
      }
    })),

    addUMLRelationship: (relationship) => set((state) => ({
      stageData: {
        ...state.stageData,
        'uml': {
          ...state.stageData['uml'],
          relationships: [...state.stageData['uml'].relationships, relationship],
          isDirty: true
        }
      }
    })),

    removeUMLRelationship: (relationshipId) => set((state) => ({
      stageData: {
        ...state.stageData,
        'uml': {
          ...state.stageData['uml'],
          relationships: state.stageData['uml'].relationships.filter(r => r.id !== relationshipId),
          isDirty: true
        }
      }
    })),

    setUMLDiagramType: (type) => set((state) => ({
      stageData: {
        ...state.stageData,
        'uml': {
          ...state.stageData['uml'],
          diagramType: type,
          isDirty: true
        }
      }
    })),

    // Data modeling methods
    updateDataModelingContent: (content) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          content,
          isDirty: true
        }
      }
    })),

    updateDataModelingEntities: (entities) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          entities,
          isDirty: true
        }
      }
    })),

    updateDataModelingRelationships: (relationships) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          relationships,
          isDirty: true
        }
      }
    })),

    updateDataModelingFKRelationships: (fkRelationships) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          fkRelationships,
          isDirty: true
        }
      }
    })),

    updateDataModelingComments: (comments) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          comments,
          isDirty: true
        }
      }
    })),

    updateDataModelingZoom: (zoom) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          zoom,
          isDirty: true
        }
      }
    })),

    updateDataModelingPan: (panX, panY) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          panX,
          panY,
          isDirty: true
        }
      }
    })),

    addDataModelingEntity: (entity) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          entities: [...state.stageData['data-modeling'].entities, entity],
          isDirty: true
        }
      }
    })),

    removeDataModelingEntity: (entityId) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          entities: state.stageData['data-modeling'].entities.filter(e => e.id !== entityId),
          relationships: state.stageData['data-modeling'].relationships.filter(r => 
            r.fromEntityId !== entityId && r.toEntityId !== entityId
          ),
          fkRelationships: state.stageData['data-modeling'].fkRelationships.filter(r => 
            r.fromEntityId !== entityId && r.toEntityId !== entityId
          ),
          isDirty: true
        }
      }
    })),

    updateDataModelingEntity: (entityId, updates) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          entities: state.stageData['data-modeling'].entities.map(e =>
            e.id === entityId ? { ...e, ...updates } : e
          ),
          isDirty: true
        }
      }
    })),

    addDataModelingRelationship: (relationship) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          relationships: [...state.stageData['data-modeling'].relationships, relationship],
          isDirty: true
        }
      }
    })),

    removeDataModelingRelationship: (relationshipId) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          relationships: state.stageData['data-modeling'].relationships.filter(r => r.id !== relationshipId),
          isDirty: true
        }
      }
    })),

    addDataModelingComment: (comment) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          comments: [...state.stageData['data-modeling'].comments, comment],
          isDirty: true
        }
      }
    })),

    removeDataModelingComment: (commentId) => set((state) => ({
      stageData: {
        ...state.stageData,
        'data-modeling': {
          ...state.stageData['data-modeling'],
          comments: state.stageData['data-modeling'].comments.filter(c => c.id !== commentId),
          isDirty: true
        }
      }
    })),

    // Dirty state management
    markCodingDirty: (problemId) => set((state) => {
      const current = state.stageData.coding[problemId]
      if (!current) return state
      
      return {
        stageData: {
          ...state.stageData,
          coding: {
            ...state.stageData.coding,
            [problemId]: { ...current, isDirty: true }
          }
        }
      }
    }),

    markDatabaseDirty: (problemId) => set((state) => {
      const current = state.stageData['database-questions'][problemId]
      if (!current) return state
      
      return {
        stageData: {
          ...state.stageData,
          'database-questions': {
            ...state.stageData['database-questions'],
            [problemId]: { ...current, isDirty: true }
          }
        }
      }
    }),

    markStageDirty: (stage) => set((state) => ({
      stageData: {
        ...state.stageData,
        [stage]: {
          ...state.stageData[stage],
          isDirty: true
        }
      }
    })),

    markCodingClean: (problemId) => set((state) => {
      const current = state.stageData.coding[problemId]
      if (!current) return state
      
      return {
        stageData: {
          ...state.stageData,
          coding: {
            ...state.stageData.coding,
            [problemId]: {
              ...current,
              isDirty: false,
              lastSaved: new Date().toISOString()
            }
          }
        }
      }
    }),

    markDatabaseClean: (problemId) => set((state) => {
      const current = state.stageData['database-questions'][problemId]
      if (!current) return state
      
      return {
        stageData: {
          ...state.stageData,
          'database-questions': {
            ...state.stageData['database-questions'],
            [problemId]: {
              ...current,
              isDirty: false,
              lastSaved: new Date().toISOString()
            }
          }
        }
      }
    }),

    markStageClean: (stage) => set((state) => ({
      stageData: {
        ...state.stageData,
        [stage]: {
          ...state.stageData[stage],
          isDirty: false,
          lastSaved: new Date().toISOString()
        }
      }
    })),

    // Data getters
    getCodingData: (problemId) => {
      const data = get().stageData.coding[problemId]
      return data || { code: '', language: null, boilerplate: '', isDirty: false, lastSaved: null }
    },

    getCodingProblemMeta: (problemId) => {
      return get().codingProblemMeta[problemId]
    },

    setCodingProblemMeta: (problemId, meta) => set((state) => ({
      codingProblemMeta: {
        ...state.codingProblemMeta,
        [problemId]: {
          problemId: meta.problemId ?? state.codingProblemMeta[problemId]?.problemId ?? null,
          assignmentOrder: meta.assignmentOrder ?? state.codingProblemMeta[problemId]?.assignmentOrder ?? null,
          assignmentUuid: meta.assignmentUuid ?? state.codingProblemMeta[problemId]?.assignmentUuid ?? null,
          starterCodeMap: meta.starterCodeMap ?? state.codingProblemMeta[problemId]?.starterCodeMap ?? {}
        }
      }
    })),

    getDatabaseData: (problemId) => {
      const data = get().stageData['database-questions'][problemId]
      return data || { query: '', isDirty: false, lastSaved: null }
    },

    getStageData: (stage) => {
      return get().stageData[stage]
    },

    getSystemDesignData: () => {
      return get().stageData['system-design']
    },

    getUMLData: () => {
      return get().stageData['uml']
    },

    getDataModelingData: () => {
      return get().stageData['data-modeling']
    },

    getWhiteboardData: () => {
      return get().stageData.whiteboard
    },
    setSystemDesignPrompt: (prompt) => set({ systemDesignPrompt: prompt }),
    getSystemDesignPrompt: () => get().systemDesignPrompt,

    // Reset
    resetStore: () => set({
      activeStage: 'guidelines',
      activeProblemId: null,
      sessionId: null,
      interviewId: null,
      stageData: createInitialStageData(),
      codingProblemMeta: {},
      systemDesignPrompt: null
    })
  }))
)
